import React from 'react';
import * as PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';

const StackedBarChart = ({
  chartData,
  classNames = ''
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const chartHeight = isMobile ? 400 : 500;
  
  return (
    <div style={{ height: `${chartHeight}px`, position: 'relative' }}>
      <Bar
        data={chartData}
        className={classNames}
        options={{
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: true,
            position: isMobile ? 'bottom' : 'top',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 15,
              padding: isMobile ? 8 : 10,
              font: {
                size: isMobile ? 9 : 12
              }
            }
          },
          tooltip: {
            enabled: true,
            mode: 'point',
            intersect: false,
            callbacks: {
              beforeTitle: function(tooltipItems) {
                if (tooltipItems.length > 0) {
                  this._hoveredStack = chartData.datasets[tooltipItems[0].datasetIndex].stack;
                  this._hoveredDataIndex = tooltipItems[0].dataIndex;
                }
                return '';
              },
              title: function(tooltipItems) {
                if (tooltipItems.length > 0) {
                  const stack = chartData.datasets[tooltipItems[0].datasetIndex].stack;
                  return `${tooltipItems[0].label} - ${stack}`;
                }
                return '';
              },
              beforeBody: function(tooltipItems) {
                const lines = [];
                if (tooltipItems.length > 0) {
                  const hoveredStack = chartData.datasets[tooltipItems[0].datasetIndex].stack;
                  const dataIndex = tooltipItems[0].dataIndex;
                  
                  chartData.datasets.forEach((dataset, idx) => {
                    if (dataset.stack === hoveredStack && dataset.data[dataIndex] > 0) {
                      const label = dataset.label.replace(dataset.stack + ' - ', '');
                      const value = dataset.data[dataIndex];
                      lines.push(`${label}: ${value}`);
                    }
                  });
                }
                return lines.reverse();
              },
              label: function(context) {
                return '';
              },
              footer: function(tooltipItems) {
                if (tooltipItems.length > 0) {
                  const hoveredStack = chartData.datasets[tooltipItems[0].datasetIndex].stack;
                  const dataIndex = tooltipItems[0].dataIndex;
                  
                  let total = 0;
                  chartData.datasets.forEach((dataset) => {
                    if (dataset.stack === hoveredStack && dataset.data[dataIndex]) {
                      total += dataset.data[dataIndex];
                    }
                  });
                  
                  return `Total: ${total}`;
                }
                return '';
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              callback: function(value, index, ticks) {
                const label = this.getLabelForValue(value);
                const paymentMethods = new Set();
                chartData.datasets.forEach(dataset => {
                  if (dataset.data[index] > 0) {
                    paymentMethods.add(dataset.stack);
                  }
                });
                
                if (isMobile) {
                  // On mobile, just show date without payment methods
                  return label;
                }
                // Return array of lines: [date, payment methods]
                const methods = Array.from(paymentMethods).join(', ');
                return [label, methods];
              },
              maxRotation: isMobile ? 45 : 0,
              minRotation: 0,
              autoSkip: true,
              maxTicksLimit: isMobile ? 6 : 20,
              font: {
                size: isMobile ? 9 : 11
              }
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              display: true
            },
            ticks: {
              precision: 0
            }
          }
        }
      }}
      />
    </div>
  );
};

export default StackedBarChart;

StackedBarChart.propTypes = {
  chartData: PropTypes.instanceOf(Object).isRequired,
  classNames: PropTypes.string
};
